import { useEffect, useState } from 'react';

interface TermPageContentData {
  heading: string;
  content: string;
}

export type TermPageContentResponse =
  | { status: 'loading'; isLoading: true; isError: false; isSuccess: false; data: undefined; error: null }
  | { status: 'error'; isLoading: false; isError: true; isSuccess: false; data: undefined; error: string }
  | { status: 'success'; isLoading: false; isError: false; isSuccess: true; data: TermPageContentData; error: null };

type TermPageContentState = Pick<TermPageContentResponse, 'status' | 'data' | 'error'>;

const HEADING_ERROR = 'Error: No Content Available';
const CONTENT_ERROR =
  'The content was retrieved, but there’s nothing available to display. Please check the content at the provided URL and try again later.';

const DEFAULT_STATE = {
  status: 'loading',
  error: null,
  data: undefined,
} as const;

const contentCache = new Map<string, TermPageContentData>();

export function useModalPageContent(href: string) {
  const [state, setState] = useState<TermPageContentState>(DEFAULT_STATE);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    const fetchContent = async () => {
      setState(DEFAULT_STATE);

      if (contentCache.has(href)) {
        const data = contentCache.get(href) as TermPageContentData;
        setState({
          status: 'success',
          error: null,
          data,
        });
        return;
      }

      try {
        const response = await fetch(href, { signal });
        const html = await response.text();

        const parser = new DOMParser();
        const virtualDoc = parser.parseFromString(html, 'text/html');

        const heading =
          (virtualDoc.querySelector('.js-modal-header') as HTMLHeadingElement)?.innerText || HEADING_ERROR;
        const content = (virtualDoc.querySelector('.js-modal-body') as HTMLDivElement)?.innerHTML || CONTENT_ERROR;

        const data = { heading, content };
        contentCache.set(href, data);

        setState({ status: 'success', error: null, data });
      } catch (err: unknown) {
        if (err instanceof Error && err.name === 'AbortError') {
          console.log('Fetch aborted'); // handle it gracefully — aborts are expected during useEffect cleanup
        } else {
          console.error('Error fetching modal content:', err);
          setState({
            status: 'error',
            data: undefined,
            error: 'Our server is being stubborn. Please try again later.',
          });
        }
      }
    };

    fetchContent();

    return () => {
      controller.abort();
    };
  }, [href]);

  return {
    ...state,
    isLoading: state.status === 'loading',
    isError: state.status === 'error',
    isSuccess: state.status === 'success',
  } as TermPageContentResponse;
}
